"use client";

// ============================================================
// CLUBHUB – Anwesenheit abhaken
// ============================================================
// Fuer die Uebungsleitung am Spielfeldrand: grosse Knoepfe „da“ und
// „fehlt“ je Person, oben die Quote. „Zusagen uebernehmen“ setzt alle,
// die zugesagt haben, auf anwesend — danach werden nur die
// Ausnahmen getippt. Bei Serien steht daneben, wie oft jemand in den
// letzten zehn Terminen da war.
//
// Gespeichert wird jeder Tipp sofort (`EventResponse.attended` je
// Vorkommen, `anwesenheitSetzen`); geht es schief, springt er zurueck.
// Daraus entstehen spaeter Teilnehmerlisten fuer Zuschussantraege und
// die Stunden fuer die Uebungsleiterpauschale.
//
// Gaeste (Probetraining) haben noch keinen Kontakt — mit Namen notiert,
// legt `gastErfassen` einen Kontakt „Interessent“ an (mit Vermerk im
// Verlauf) und traegt ihn als anwesend ein.
// ============================================================
import { useMemo, useState } from "react";
import Link from "next/link";
import { Check, ClipboardCheck, RotateCcw, UserPlus, X } from "lucide-react";
import { Fuge, Schiene, Zaehlknopf } from "@/components/liste";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { confirmDialog } from "@/components/ui/confirm";
import { toast } from "@/components/ui/toast";
import { TonBadge } from "@/components/mitglieder/status-badge";
import { RSVP_STATUS } from "@/lib/labels";
import { formatTagMitWochentag, tagVon, uhrzeit } from "@/lib/kalender/tag";
import { quote } from "@/lib/kalender/zusagen";
import type { AntwortView, AnwesenheitsQuote, PersonKurz } from "@/lib/kalender/views";
import { errorOf } from "@/lib/action-result";
import { anwesenheitSetzen, gastErfassen } from "@/app/(app)/kalender/actions";
import { cn } from "@/lib/utils";

type Filter = "" | "da" | "fehlt" | "offen";

export function AnwesenheitListe({
  eingeladene,
  antworten,
  quoten,
  beginn,
  jetzt,
  zuletzt,
  rsvp,
  eventId,
  occurrence,
  darf = true,
}: {
  eventId: string;
  occurrence: string | null;
  /** Recht „Anwesenheit erfassen“. */
  darf?: boolean;
  eingeladene: PersonKurz[];
  antworten: AntwortView[];
  quoten: AnwesenheitsQuote[];
  beginn: string;
  jetzt: string;
  /** Das letzte stattgefundene Vorkommen einer Serie — fuer den Hinweis bei kuenftigen. */
  zuletzt: { key: string; startsAt: string } | null;
  rsvp: boolean;
}) {
  const antwortVon = useMemo(() => new Map(antworten.map((a) => [a.contactId, a])), [antworten]);
  const [neueGaeste, setNeueGaeste] = useState<PersonKurz[]>([]);
  const personen = useMemo(() => [...eingeladene, ...neueGaeste.filter((g) => !eingeladene.some((p) => p.id === g.id))], [eingeladene, neueGaeste]);
  const [da, setDa] = useState<Map<string, boolean | null>>(() => new Map(eingeladene.map((p) => [p.id, antwortVon.get(p.id)?.attended ?? null])));
  const [gast, setGast] = useState("");
  const [gastSpeichert, setGastSpeichert] = useState(false);
  const [filter, setFilter] = useState<Filter>("");
  const quoteVon = useMemo(() => new Map(quoten.map((q) => [q.contactId, q])), [quoten]);
  const kuenftig = beginn.slice(0, 10) > jetzt.slice(0, 10);
  const gesperrt = kuenftig || !darf;
  const wert = (id: string): boolean | null => (da.has(id) ? (da.get(id) ?? null) : (antwortVon.get(id)?.attended ?? null));

  const werte = personen.map((p) => wert(p.id));
  const anwesend = werte.filter((w) => w === true).length;
  const fehlt = werte.filter((w) => w === false).length;
  const offen = werte.filter((w) => w === null).length;
  const gesamt = personen.length;

  /** Mehrere Haken auf einmal setzen — sofort zeigen, dann speichern, bei Fehler zurueck. */
  async function speichern(aenderungen: { contactId: string; attended: boolean | null }[]): Promise<boolean> {
    if (aenderungen.length === 0) return true;
    const sicherung = da;
    setDa((m) => {
      const neu = new Map(m);
      for (const a of aenderungen) neu.set(a.contactId, a.attended);
      return neu;
    });
    const r = await anwesenheitSetzen(eventId, occurrence, aenderungen);
    const fehler = errorOf(r);
    if (fehler) {
      setDa(sicherung);
      toast.error(fehler);
      return false;
    }
    return true;
  }

  function setzen(id: string, w: boolean | null) {
    void speichern([{ contactId: id, attended: w }]);
  }

  async function zusagenUebernehmen() {
    const aenderungen: { contactId: string; attended: boolean }[] = [];
    for (const p of personen) {
      const a = antwortVon.get(p.id);
      if (wert(p.id) !== null) continue;
      if (a?.status === "YES") aenderungen.push({ contactId: p.id, attended: true });
      else if (a?.status === "NO") aenderungen.push({ contactId: p.id, attended: false });
    }
    if (!(await speichern(aenderungen))) return;
    const n = aenderungen.filter((a) => a.attended).length;
    toast.success("Zusagen übernommen", { description: n ? "Jetzt nur noch die Ausnahmen antippen." : "Es gab nichts zu übernehmen." });
  }

  async function zuruecksetzen() {
    const ja = await confirmDialog({ title: "Anwesenheit zurücksetzen?", description: "Alle Haken dieses Termins werden entfernt. Als Gast angelegte Interessenten bleiben in den Mitgliedern.", confirmLabel: "Zurücksetzen", danger: true });
    if (!ja) return;
    await speichern(personen.filter((p) => wert(p.id) !== null).map((p) => ({ contactId: p.id, attended: null })));
  }

  async function gastDazu() {
    const name = gast.trim();
    if (name.length < 2) return toast.error("Bitte einen Namen eingeben.");
    setGastSpeichert(true);
    const r = await gastErfassen(eventId, occurrence, name);
    setGastSpeichert(false);
    const fehler = errorOf(r);
    if (fehler || !r.success) return void toast.error(fehler ?? "Das hat nicht geklappt.");
    const neu: PersonKurz = { id: r.id, name: r.displayName, firstName: r.firstName ?? "", lastName: r.lastName, memberNumber: null, funktion: null, consentInternet: false, minderjaehrig: false, gast: true };
    setNeueGaeste((g) => [...g, neu]);
    setDa((m) => new Map(m).set(r.id, true));
    setGast("");
    toast.success(`${r.displayName} als Gast notiert`, { description: "Als Interessent angelegt, mit Vermerk im Verlauf — für den Aufnahmeantrag später." });
  }

  const gezeigt = personen.filter((p) => {
    const w = wert(p.id);
    return !filter || (filter === "da" ? w === true : filter === "fehlt" ? w === false : w === null);
  });

  return (
    <div className="space-y-4">
      {kuenftig && (
        <div className="border-border bg-bg-primary text-body-sm text-text-primary flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2">
          <ClipboardCheck className="text-text-tertiary h-4 w-4 shrink-0" aria-hidden />
          <span className="min-w-0 flex-1">Die Anwesenheit wird ab dem Tag des Termins erfasst — vorher stehen hier nur die Eingeladenen.</span>
          {zuletzt && (
            <Link href={`/kalender/${zuletzt.key}/anwesenheit`} className="text-caption text-accent hover:text-accent-hover font-medium underline-offset-2 hover:underline">
              Zum letzten Termin ({formatTagMitWochentag(tagVon(zuletzt.startsAt))})
            </Link>
          )}
        </div>
      )}

      <Card>
        <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-3 p-4">
          <div>
            <p className="text-overline text-text-tertiary">Anwesend</p>
            <p className="text-display-lg text-text-primary tabular-nums">
              {anwesend}
              <span className="text-heading-md text-text-tertiary"> / {gesamt}</span>
            </p>
          </div>
          <div className="min-w-40 flex-1">
            <div className="text-caption text-text-secondary mb-1 flex justify-between tabular-nums">
              <span>Quote {quote(anwesend, gesamt)}</span>
              <span>
                {fehlt} fehlen · {offen} nicht erfasst
              </span>
            </div>
            <div className="bg-bg-tertiary flex h-2 overflow-hidden rounded-full">
              <span className="bg-success" style={{ width: `${gesamt ? (anwesend / gesamt) * 100 : 0}%` }} />
              <span className="bg-danger/60" style={{ width: `${gesamt ? (fehlt / gesamt) * 100 : 0}%` }} />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {rsvp && (
              <Button variant="outline" onClick={zusagenUebernehmen} disabled={gesperrt}>
                <Check className="h-4 w-4" aria-hidden />
                Zusagen übernehmen
              </Button>
            )}
            <Button variant="ghost" onClick={zuruecksetzen} disabled={gesperrt}>
              <RotateCcw className="h-4 w-4" aria-hidden />
              Zurücksetzen
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-2">
        <Schiene>
          <Zaehlknopf label="Alle" anzahl={personen.length} aktiv={!filter} onClick={() => setFilter("")} />
          <Fuge />
          <Zaehlknopf label="Da" anzahl={anwesend} aktiv={filter === "da"} onClick={() => setFilter(filter === "da" ? "" : "da")} />
          <Zaehlknopf label="Fehlt" anzahl={fehlt} aktiv={filter === "fehlt"} onClick={() => setFilter(filter === "fehlt" ? "" : "fehlt")} />
          <Zaehlknopf label="Nicht erfasst" anzahl={offen} aktiv={filter === "offen"} onClick={() => setFilter(filter === "offen" ? "" : "offen")} />
        </Schiene>
        <form
          className="flex min-w-0 items-center gap-2 max-md:w-full"
          onSubmit={(e) => {
            e.preventDefault();
            gastDazu();
          }}
        >
          <Input value={gast} onChange={(e) => setGast(e.target.value)} placeholder="Gast oder Probetraining …" aria-label="Name eines Gasts" className="h-9 w-56 max-md:w-full" disabled={gesperrt} wrapperClassName="max-md:flex-1" />
          <Button type="submit" variant="ghost" disabled={gesperrt || !gast.trim()} loading={gastSpeichert}>
            <UserPlus className="h-4 w-4" aria-hidden />
            <span className="max-sm:sr-only">Gast</span>
          </Button>
        </form>
      </div>

      <Card className="overflow-hidden">
        {gezeigt.length === 0 ? (
          <CardContent className="p-5">
            <EmptyState title="Niemand in dieser Auswahl" description="Wähle oben „Alle“, um die ganze Liste zu sehen." />
          </CardContent>
        ) : (
          <ul className="divide-border-subtle divide-y">
            {gezeigt.map((p) => {
              const w = wert(p.id);
              const a = antwortVon.get(p.id);
              const q = quoteVon.get(p.id);
              return (
                <li key={p.id} className={cn("flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5", w === true && "bg-success-subtle/40")}>
                  <Avatar firstName={p.firstName} lastName={p.lastName} size="sm" />
                  <div className="min-w-0 flex-1 basis-36">
                    <p className="text-body-sm text-text-primary flex min-w-0 items-center gap-2 font-medium">
                      <span className="truncate">{p.name}</span>
                      {p.gast && <TonBadge ton="info">Gast</TonBadge>}
                    </p>
                    <p className="text-caption text-text-tertiary flex flex-wrap items-center gap-x-2">
                      {rsvp && a && a.status !== "PENDING" ? <span>{RSVP_STATUS[a.status].label}</span> : rsvp ? <span>keine Antwort</span> : null}
                      {p.funktion && <span>{p.funktion}</span>}
                    </p>
                  </div>
                  {q && q.von > 0 && (
                    <div className="w-20 shrink-0" title={`In den letzten ${q.von} Terminen ${q.anwesend}-mal da`}>
                      <p className="text-caption text-text-secondary text-right tabular-nums">
                        {q.anwesend}/{q.von}
                      </p>
                      <div className="bg-bg-tertiary h-1 overflow-hidden rounded-full">
                        <div className={cn("h-full rounded-full", q.anwesend / q.von >= 0.7 ? "bg-success" : q.anwesend / q.von >= 0.4 ? "bg-warning" : "bg-danger")} style={{ width: `${(q.anwesend / q.von) * 100}%` }} />
                      </div>
                    </div>
                  )}
                  <div className="flex shrink-0 gap-1" role="group" aria-label={`Anwesenheit ${p.name}`}>
                    <button
                      type="button"
                      disabled={gesperrt}
                      aria-pressed={w === true}
                      onClick={() => setzen(p.id, w === true ? null : true)}
                      className={cn(
                        "text-body-sm duration-fast flex h-10 items-center gap-1.5 rounded-lg px-3 transition-colors disabled:opacity-40",
                        w === true ? "bg-success-subtle text-success ring-success/30 font-medium ring-1 ring-inset" : "bg-bg-secondary text-text-secondary hover:text-text-primary",
                      )}
                    >
                      <Check className="h-4 w-4" aria-hidden />
                      Da
                    </button>
                    <button
                      type="button"
                      disabled={gesperrt}
                      aria-pressed={w === false}
                      onClick={() => setzen(p.id, w === false ? null : false)}
                      className={cn(
                        "text-body-sm duration-fast flex h-10 items-center gap-1.5 rounded-lg px-3 transition-colors disabled:opacity-40",
                        w === false ? "bg-danger-subtle text-danger ring-danger/30 font-medium ring-1 ring-inset" : "bg-bg-secondary text-text-secondary hover:text-text-primary",
                      )}
                    >
                      <X className="h-4 w-4" aria-hidden />
                      Fehlt
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
      <p className="text-caption text-text-tertiary px-1">
        Jeder Tipp wird sofort gespeichert. Aus der Anwesenheit entstehen Teilnehmerlisten für Zuschüsse und die Stunden der Übungsleitung. Beginn: {formatTagMitWochentag(tagVon(beginn))}, {uhrzeit(beginn)} Uhr.
      </p>
    </div>
  );
}
